import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type FeedbackSummary = components["schemas"]["FeedbackSummary"];
export type FeedbackDetail = components["schemas"]["FeedbackDetail"];
export type FeedbackEntry = components["schemas"]["FeedbackEntry"];
export type FeedbackContext = components["schemas"]["FeedbackContext"];
export type FeedbackType = NonNullable<FeedbackSummary["type"]>;
export type FeedbackRating = NonNullable<FeedbackSummary["rating"]>;

export const TYPES: FeedbackType[] = ["BUG", "IDEA", "QUESTION"];
export const RATINGS: Record<FeedbackType, FeedbackRating[]> = {
  BUG: ["BLOCKING", "HINDERS", "MINOR"],
  IDEA: ["DAILY", "WEEKLY", "SOMETIMES"],
  QUESTION: [],
};

const LIST_PATH = "/api/v1/feedback";
const UNREAD_PATH = "/api/v1/feedback/unread";
const DETAIL_PATH = "/api/v1/feedback/{id}";
const REPLY_PATH = "/api/v1/feedback/{id}/messages";

export function useFeedbackList(params: { scope: "mine" | "roastery"; closed: boolean; page: number }) {
  const api = useApi();
  return api.query.useQuery("get", LIST_PATH, {
    params: { query: { scope: params.scope, closed: params.closed, page: params.page, size: 20 } },
  });
}

/** The number next to the "My reports" tab: answers not read yet. */
export function useFeedbackUnread(enabled = true) {
  const api = useApi();
  return api.query.useQuery("get", UNREAD_PATH, {}, { enabled, staleTime: 60_000 });
}

export function useFeedback(id: string) {
  const api = useApi();
  return api.query.useQuery("get", DETAIL_PATH, { params: { path: { id } } }, { enabled: Boolean(id) });
}

function useInvalidateFeedback() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["get", LIST_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", UNREAD_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", DETAIL_PATH] }),
    ]);
}

export function useReplyFeedback() {
  const api = useApi();
  const invalidate = useInvalidateFeedback();
  return api.query.useMutation("post", REPLY_PATH, { onSuccess: invalidate });
}

export interface SubmitFeedback {
  type: FeedbackType;
  rating?: FeedbackRating;
  title: string;
  body: string;
  screen?: string;
  context?: FeedbackContext;
  files: File[];
}

/**
 * Sends a report. Not a generated mutation: the body is `FormData` — the report as a JSON part and the
 * screenshots beside it — which `openapi-fetch` passes through so the browser sets the boundary itself.
 * A refusal is thrown as the problem document, so `useDialogError` translates it like any other.
 */
export function useSubmitFeedback() {
  const api = useApi();
  const invalidate = useInvalidateFeedback();
  return async ({ files, ...report }: SubmitFeedback): Promise<FeedbackSummary> => {
    const form = new FormData();
    form.append("report", new Blob([JSON.stringify(report)], { type: "application/json" }));
    files.forEach((file) => form.append("files", file, file.name));
    const { data, error } = await api.fetch.POST(LIST_PATH, { body: form as never });
    if (error || !data) {
      throw error ?? new Error("report not sent");
    }
    await invalidate();
    return data;
  };
}

/** A screenshot of a report, fetched with the token and handed back as an object URL. */
export function useAttachmentUrl() {
  const api = useApi();
  return async (id: string, fileId: string): Promise<string> => {
    const { data } = await api.fetch.GET("/api/v1/feedback/{id}/attachments/{fileId}", {
      params: { path: { id, fileId } },
      parseAs: "blob",
    });
    if (!data) {
      throw new Error("attachment not found");
    }
    return URL.createObjectURL(data as Blob);
  };
}
